import { useEffect, useState } from "react";
import useInputState from "../../../hooks/useInputState";
import { useMutation } from "@apollo/react-hooks";
import { UpdateAccountInfo } from "./mutation";
import UploadHelper from "../../../helpers/upload";
import { isEmail, isURL } from "validator";
import { GetProfile } from "../queries";
import DefaultToaster from "../../../atoms/DefaultToaster";
import { Intent } from "@blueprintjs/core";
import useAuthState from "../../../hooks/useAuthState";
import useMaskedInputState from "../../../hooks/useMaskedInputState";
import useTutorialsState from "../../../hooks/useTutorialsState";
import useTagAccount from "../../../hooks/useTagAccount";

export const useChangeProfile = ({ profile }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState();
  const [name, setName] = useMaskedInputState("", {
    regex: /[^ ]/,
    maxLength: 255
  });
  const [email, setEmail] = useInputState("");
  const [bio, setBio] = useMaskedInputState("", {
    regex: /[^ ]/,
    maxLength: 4000
  });
  const [supportEmail, setSupportEmail] = useMaskedInputState("", {
    regex: /[^ ]/,
    maxLength: 255
  });
  const [termsOfServiceUrl, setTermsOfServiceUrl] = useMaskedInputState("", {
    maxLength: 500
  });
  const [privacyPolicyUrl, setPrivacyPolicyUrl] =useMaskedInputState("", {
    maxLength: 500
  });
  const [refundPolicyUrl, setRefundPolicyUrl] = useMaskedInputState("", {
    maxLength: 500
  });
  const [website, setWebsite] = useInputState("");
  const [image, setImage] = useState("");
  const [imageFile, setImageFile] = useState("");
  const [savedPicture, setSavedPicture] = useState("");
  const { getHasCompletedTutorial, completeTutorial } = useTutorialsState();
  const { tagAccount } = useTagAccount();

  const [statementDescriptor, setStatementDescriptor] = useMaskedInputState(
    "",
    {
      regex: /^[a-zA-Z0-9@^$.\-#+~_\b ]+$/,
      maxLength: 14,
      convertToLowerCase: false
    }
  );
  const [errorField, setErrorField] = useState("");
  const { loadAuthSession } = useAuthState();

  const [updateProfileInfo] = useMutation(UpdateAccountInfo, {
    refetchQueries: [{ query: GetProfile }]
  });

  const onImageChange = (file) => {
    const jsFile = new File(file, "profile_image");
    setImageFile(jsFile);
    const dataReader = new FileReader();
    dataReader.onload = (event) => {
      setImage(event.target.result);
    };
    dataReader.readAsDataURL(jsFile);
  };

  const saveChanges = async () => {
    setIsSaving(true);
    setError(undefined);

    try {
      let picture = undefined;

      if (savedPicture !== image) {
        const { signedUrl, url } = await UploadHelper.getSignedUrl(
          UploadHelper.AVATAR
        );
        await UploadHelper.uploadToS3(signedUrl, imageFile);
        picture = url;
      }
      if(!name){
        setErrorField("name");
        setIsSaving(false);
      }
      else if (!bio){
        setErrorField("bio");
        setIsSaving(false);
      }
      else if (!website  || website && !isURL(website)) {
        setErrorField("website");
        setIsSaving(false);
      } 
      else if (supportEmail && !isEmail(supportEmail)) {
        setErrorField("supportEmail");
        setIsSaving(false);
      } else if (termsOfServiceUrl && !isURL(termsOfServiceUrl)) {
        setErrorField("termsOfServiceUrl");
        setIsSaving(false);
      } else if (privacyPolicyUrl && !isURL(privacyPolicyUrl)) {
        setErrorField("privacyPolicyUrl");
        setIsSaving(false);
      } else if (refundPolicyUrl && !isURL(refundPolicyUrl)) {
        setErrorField("refundPolicyUrl");
        setIsSaving(false);
      } else if (!statementDescriptor) {
        setErrorField("statementDescriptor");
        setIsSaving(false);
      } else {
        setErrorField("");
        const isNmiAccount = profile && profile.isNmiAccount;
        await updateProfileInfo({
          variables: {
            account: {
              picture,
              name,
              bio,
              refundPolicyUrl,
              privacyPolicyUrl,
              termsOfServiceUrl,
              website,
              supportEmail,
              ...(!isNmiAccount && { statementDescriptor })
            }
          }
        });
        setIsSaving(false);

        if (statementDescriptor) {
          const hasAddedStatementDescriptor = getHasCompletedTutorial(
            "Add Statement Descriptor"
          );

          if (!hasAddedStatementDescriptor) {
            await completeTutorial({
              variables: {
                name: "Add Statement Descriptor",
                version: 1
              }
            });
          }
        }

        if (
          refundPolicyUrl &&
          privacyPolicyUrl &&
          termsOfServiceUrl &&
          supportEmail
        ) {
          const hasAddedCustomerSupportInfo = getHasCompletedTutorial(
            "Add Customer Support Info"
          );

          if (!hasAddedCustomerSupportInfo) {
            await completeTutorial({
              variables: {
                name: "Add Customer Support Info",
                version: 1
              }
            });

            await tagAccount("Customer Support Info Provided");
          }
        }

        DefaultToaster.show({
          message: "Profile saved!",
          icon: "tick",
          intent: Intent.SUCCESS
        });
      }
    } catch (err) {
      setError({
        message: "An error occurred while updating record."
      });
    }
  };

  useEffect(() => {
    if (profile && profile.length !== 0) {
      setName(profile.name);
      setBio(profile.bio);
      setEmail(profile.email);
      setImage(profile.picture);
      setWebsite(profile.website);
      setSupportEmail(profile.supportEmail);
      setPrivacyPolicyUrl(profile.policies.privacyPolicyUrl);
      setRefundPolicyUrl(profile.policies.refundPolicyUrl);
      setTermsOfServiceUrl(profile.policies.termsOfServiceUrl);
      setSavedPicture(profile.picture);
      setStatementDescriptor(profile.statementDescriptor);
    }
  }, [profile]);

  return {
    error,
    name,
    email,
    bio,
    supportEmail,
    termsOfServiceUrl,
    refundPolicyUrl,
    privacyPolicyUrl,
    website,
    image,
    statementDescriptor,
    onImageChange,
    setName,
    setEmail,
    setBio,
    setSupportEmail,
    setTermsOfServiceUrl,
    setPrivacyPolicyUrl,
    setRefundPolicyUrl,
    setWebsite,
    setStatementDescriptor,
    errorField,
    isSaving,
    saveChanges
  };
};
